import type { GridApi } from '@mui/x-data-grid-pro';
import { gridExpandedSortedRowIdsSelector, gridRenderContextSelector } from '@mui/x-data-grid-pro';
import * as React from 'react';

const OVERSCAN_ROWS = 10;
const DEBOUNCE_MS = 50;

type RenderContextWindow = { firstRowIndex: number; lastRowIndex: number };

type ComputeResult = {
  pageIndices: readonly number[];
  itemIds: readonly number[];
};

const computeVisible = (
  apiRef: React.RefObject<GridApi>,
  context: RenderContextWindow | undefined,
  pageIndexById: ReadonlyMap<number, number>,
): ComputeResult => {
  const state = apiRef.current?.state;
  if (!state || !context || pageIndexById.size === 0) return EMPTY_RESULT;
  // biome-ignore lint/suspicious/noExplicitAny: MUI selector cannot accept GridStatePro typing
  const entries = gridExpandedSortedRowIdsSelector(state as any);
  if (!entries.length) return EMPTY_RESULT;

  // MUI's renderContext is a half-open window [firstRowIndex, lastRowIndex).
  const from = Math.max(0, context.firstRowIndex - OVERSCAN_ROWS);
  const to = Math.min(entries.length, context.lastRowIndex + OVERSCAN_ROWS);

  const seenPageIndices = new Set<number>();
  const seenItemIds = new Set<number>();
  for (let i = from; i < to; i++) {
    const id = Number(entries[i]);
    if (Number.isInteger(id) && id >= 0) {
      seenItemIds.add(id);
    }
    const pageIndex = pageIndexById.get(id);
    if (pageIndex !== undefined) seenPageIndices.add(pageIndex);
  }
  return {
    pageIndices: Array.from(seenPageIndices).sort((a, b) => a - b),
    itemIds: Array.from(seenItemIds).sort((a, b) => a - b),
  };
};

const EMPTY_RESULT: ComputeResult = { pageIndices: [], itemIds: [] };

const arraysEqual = (a: readonly number[], b: readonly number[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

type UseVisiblePageIndicesResult = {
  visiblePageIndices: readonly number[];
  visibleItemIds: readonly number[];
};

export const useVisiblePageIndices = ({
  apiRef,
  pageIndexById,
}: {
  apiRef: React.RefObject<GridApi>;
  pageIndexById: ReadonlyMap<number, number>;
}): UseVisiblePageIndicesResult => {
  const [pageIndices, setPageIndices] = React.useState<readonly number[]>([]);
  const [debouncedItemIds, setDebouncedItemIds] = React.useState<readonly number[]>([]);
  const latestContextRef = React.useRef<RenderContextWindow | undefined>(undefined);
  const latestItemIdsRef = React.useRef<readonly number[]>([]);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const recompute = React.useCallback(() => {
    const result = computeVisible(apiRef, latestContextRef.current, pageIndexById);
    setPageIndices((prev) => (arraysEqual(prev, result.pageIndices) ? prev : result.pageIndices));
    latestItemIdsRef.current = result.itemIds;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedItemIds((prev) =>
        arraysEqual(prev, latestItemIdsRef.current) ? prev : latestItemIdsRef.current,
      );
    }, DEBOUNCE_MS);
  }, [apiRef, pageIndexById]);

  // The event payload is the source of truth; state.virtualization.renderContext
  // is committed via the virtualizer store and can lag the event.
  const handleRenderContextChange = React.useCallback(
    (renderContext: RenderContextWindow) => {
      latestContextRef.current = renderContext;
      recompute();
    },
    [recompute],
  );

  const handleRowsChange = React.useCallback(() => {
    // biome-ignore lint/suspicious/noExplicitAny: MUI selector cannot accept GridStatePro typing
    latestContextRef.current = gridRenderContextSelector(apiRef as any);
    recompute();
  }, [apiRef, recompute]);

  React.useEffect(() => {
    if (!apiRef.current) return;
    const unsubs = [
      apiRef.current.subscribeEvent('renderedRowsIntervalChange', handleRenderContextChange),
      apiRef.current.subscribeEvent('rowsSet', handleRowsChange),
      apiRef.current.subscribeEvent('rowExpansionChange', handleRowsChange),
    ];
    return () => {
      for (const unsub of unsubs) unsub();
    };
  }, [apiRef, handleRenderContextChange, handleRowsChange]);

  React.useEffect(() => {
    if (!apiRef.current) return;
    // biome-ignore lint/suspicious/noExplicitAny: MUI selector cannot accept GridStatePro typing
    latestContextRef.current = gridRenderContextSelector(apiRef as any);
    recompute();
  }, [apiRef, recompute]);

  React.useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return { visiblePageIndices: pageIndices, visibleItemIds: debouncedItemIds };
};
